import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  Chip,
  TextField,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import default_theme from "assets/themes/themes";
import {
  fetchProjectDynamosRequest,
  createLapRequest,
} from "../../../redux/projectDynamos/projectDynamosSlice";

const OPTIONAL_FIELDS = [
  { key: "solution_title", label: "Solution Title" },
  { key: "relevant_challenges", label: "Relevant Challenges" },
  { key: "specific_activities", label: "Specific Activities" },
  { key: "targets", label: "Targets" },
  { key: "geography_and_territorial_context", label: "Geography and Territorial Context" },
  { key: "integration_of_relevant_crosscutting", label: "Integration of Relevant Crosscutting" },
  { key: "forms_of_innovation_considered", label: "Forms of Innovation Considered" },
  { key: "gender_sensitive_planning_aspects", label: "Gender Sensitive Planning Aspects" },
  { key: "resources_capitals_needed", label: "Resources and Capitals Needed" },
  {
    key: "main_stakeholders_involved_and_their_contribution",
    label: "Main Stakeholders and Contribution",
  },
  { key: "main_and_other_beneficiaries", label: "Main and Other Beneficiaries" },
  { key: "target_groups_at_risk_of_exclusion", label: "Target Groups at Risk of Exclusion" },
  { key: "timeframe_m_to_m", label: "Timeframe (M to M)" },
  { key: "indicative_cost", label: "Indicative Cost" },
  { key: "indicative_funding_sources", label: "Indicative Funding Sources" },
  { key: "long_term_impact_assessment", label: "Long Term Impact Assessment" },
  { key: "communication_and_engagement", label: "Communication and Engagement" },
  { key: "sustainability_consideration", label: "Sustainability Consideration" },
  { key: "synergies_with_other_solutions", label: "Synergies with Other Solutions" },
  { key: "synergies_with_local_policies", label: "Synergies with Local Policies" },
  {
    key: "synergies_with_eu_policies_when_relevant",
    label: "Synergies with EU Policies (when relevant)",
  },
];

const createEmptyLapForm = () => ({
  mandatory: {
    brief_description: "",
    objectives_of_the_solution: "",
    relevant_rdd_and_rdd_subcategory: "",
    location_of_implementation: "",
  },
  optional: {},
  enabledOptionalKeys: [],
});

const ProjectDynamoPage = () => {
  const { projectId, dynamoId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const projectDynamosState = useSelector((state) => state.projectDynamos);
  const { userPermission } = useSelector((state) => state.projects);

  const canEdit = userPermission === "owner" || userPermission === "editor";

  const dynamosForProject = projectDynamosState.byProjectId[projectId] || {
    drafts: [],
    published: [],
  };

  const allDynamos = useMemo(
    () => [...(dynamosForProject.drafts || []), ...(dynamosForProject.published || [])],
    [dynamosForProject.drafts, dynamosForProject.published]
  );
  const dynamo = allDynamos.find((d) => d.id === dynamoId);

  const [lapDialogOpen, setLapDialogOpen] = useState(false);
  const [lapForm, setLapForm] = useState(createEmptyLapForm());

  useEffect(() => {
    if (projectId) {
      dispatch(fetchProjectDynamosRequest({ projectId }));
    }
  }, [dispatch, projectId]);

  const handleBack = () => {
    navigate(`/workspaces/projects/${projectId}`);
  };

  const openNewLapDialog = () => {
    setLapForm(createEmptyLapForm());
    setLapDialogOpen(true);
  };

  const handleMandatoryChange = (field, value) => {
    setLapForm((prev) => ({
      ...prev,
      mandatory: {
        ...prev.mandatory,
        [field]: value,
      },
    }));
  };

  const toggleOptionalField = (key) => {
    setLapForm((prev) => {
      const enabled = new Set(prev.enabledOptionalKeys);
      if (enabled.has(key)) {
        enabled.delete(key);
      } else {
        enabled.add(key);
      }
      return {
        ...prev,
        enabledOptionalKeys: Array.from(enabled),
      };
    });
  };

  const handleOptionalChange = (key, value) => {
    setLapForm((prev) => ({
      ...prev,
      optional: {
        ...prev.optional,
        [key]: value,
      },
    }));
  };

  const handleLapSubmit = () => {
    if (!projectId || !dynamoId) return;
    const { mandatory, optional, enabledOptionalKeys } = lapForm;
    // Basic validation for required fields
    if (
      !mandatory.brief_description.trim() ||
      !mandatory.objectives_of_the_solution.trim() ||
      !mandatory.relevant_rdd_and_rdd_subcategory.trim() ||
      !mandatory.location_of_implementation.trim()
    ) {
      return;
    }
    const filteredOptional = {};
    enabledOptionalKeys.forEach((key) => {
      if (optional[key]) {
        filteredOptional[key] = optional[key];
      }
    });
    dispatch(
      createLapRequest({
        projectId,
        dynamoId,
        mandatoryFields: mandatory,
        optionalFields: filteredOptional,
      })
    );
    setLapDialogOpen(false);
  };

  if (!dynamo) {
    return (
      <Box sx={{ py: 4 }}>
        <Container maxWidth="lg">
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
            Back to Project
          </Button>
          <Typography variant="h5">Dynamo not found</Typography>
        </Container>
      </Box>
    );
  }

  const laps = dynamo.laps || { drafts: [], published: [] };

  return (
    <Box sx={{ flex: 1, backgroundColor: default_theme.palette.primary.white, minHeight: "100%", py: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
            Back to Project
          </Button>
          <Typography variant="h4" sx={{ flex: 1 }}>
            {dynamo.title || "Untitled Dynamo"}
          </Typography>
          <Chip
            label={dynamo.status === "published" ? "Published" : "Saved"}
            color={dynamo.status === "published" ? "success" : "default"}
            size="small"
          />
        </Box>

        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Introduction
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {dynamo.introduction ||
              (dynamo.combinedData && dynamo.combinedData.brief_description) ||
              "No introduction provided yet."}
          </Typography>
          {dynamo.collaborators && dynamo.collaborators.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Contributors
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {dynamo.collaborators.map((c) => c.name).join(", ")}
              </Typography>
            </Box>
          )}
        </Paper>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5">Local Action Plans</Typography>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openNewLapDialog}
              sx={{
                backgroundColor: default_theme.palette.other.chatGreen,
                "&:hover": {
                  backgroundColor: default_theme.palette.other.chatGreenHover,
                },
              }}
            >
              New LAP
            </Button>
          )}
        </Box>

        <Grid container spacing={3}>
          {["drafts", "published"].map((statusKey) => {
            const label = statusKey === "drafts" ? "Saved LAPs" : "Published LAPs";
            const list = laps[statusKey] || [];
            return (
              <Grid item xs={12} md={6} key={statusKey}>
                <Paper sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography variant="h6">{label}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {list.length} LAP{list.length !== 1 ? "s" : ""}
                    </Typography>
                  </Box>
                  {list.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No LAPs {statusKey === "drafts" ? "saved yet." : "published yet."}
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                      {list.map((lap) => (
                        <Box
                          key={lap.id}
                          sx={{
                            borderRadius: 1.5,
                            border: "1px solid",
                            borderColor: "divider",
                            p: 1.5,
                          }}
                        >
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {lap.combinedData?.solution_title || lap.title || "Untitled LAP"}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {lap.combinedData?.brief_description ||
                              lap.combinedData?.objectives_of_the_solution ||
                              ""}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {lapDialogOpen && (
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0,0,0,0.3)",
              zIndex: 1300,
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: 800,
                maxHeight: "90vh",
                overflowY: "auto",
                backgroundColor: default_theme.palette.primary.white,
                borderRadius: 2,
                p: 3,
                boxShadow: default_theme.palette.other.cardShadow,
              }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                New LAP
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Mandatory fields
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Brief Description"
                    value={lapForm.mandatory.brief_description}
                    onChange={(e) => handleMandatoryChange("brief_description", e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Objectives of the Solution"
                    value={lapForm.mandatory.objectives_of_the_solution}
                    onChange={(e) =>
                      handleMandatoryChange("objectives_of_the_solution", e.target.value)
                    }
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Relevant RDD and RDD Subcategory"
                    value={lapForm.mandatory.relevant_rdd_and_rdd_subcategory}
                    onChange={(e) =>
                      handleMandatoryChange("relevant_rdd_and_rdd_subcategory", e.target.value)
                    }
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Location of Implementation"
                    value={lapForm.mandatory.location_of_implementation}
                    onChange={(e) =>
                      handleMandatoryChange("location_of_implementation", e.target.value)
                    }
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Optional fields
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                    {OPTIONAL_FIELDS.map((field) => {
                      const active = lapForm.enabledOptionalKeys.includes(field.key);
                      return (
                        <Chip
                          key={field.key}
                          label={field.label}
                          onClick={() => toggleOptionalField(field.key)}
                          variant={active ? "filled" : "outlined"}
                          color={active ? "secondary" : "default"}
                          sx={{ cursor: "pointer" }}
                        />
                      );
                    })}
                  </Box>
                  {OPTIONAL_FIELDS.filter((f) => lapForm.enabledOptionalKeys.includes(f.key)).map(
                    (field) => (
                      <TextField
                        key={field.key}
                        fullWidth
                        multiline
                        minRows={2}
                        label={field.label}
                        value={lapForm.optional[field.key] || ""}
                        onChange={(e) => handleOptionalChange(field.key, e.target.value)}
                        sx={{ mb: 2 }}
                      />
                    )
                  )}
                </Grid>
              </Grid>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
                <Button onClick={() => setLapDialogOpen(false)}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={handleLapSubmit}
                  startIcon={<AddIcon />}
                  disabled={
                    !lapForm.mandatory.brief_description.trim() ||
                    !lapForm.mandatory.objectives_of_the_solution.trim() ||
                    !lapForm.mandatory.relevant_rdd_and_rdd_subcategory.trim() ||
                    !lapForm.mandatory.location_of_implementation.trim()
                  }
                  sx={{
                    backgroundColor: default_theme.palette.other.chatGreen,
                    "&:hover": {
                      backgroundColor: default_theme.palette.other.chatGreenHover,
                    },
                  }}
                >
                  Save LAP
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default ProjectDynamoPage;

